import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { HeartPulse, Check, X, Loader2 } from "lucide-react";

// Client-facing telehealth consent gate — explicit, never pre-checked.
export default function TelehealthConsentCard({ caseId }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/telehealth`).then(({ data }) => setState(data)).catch(() => setState(null));
  }, [caseId]);
  useEffect(load, [load]);

  if (!state || !state.request) return null;

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/telehealth/consent`, { decision });
      toast.success(decision === "consent"
        ? "Consent recorded — the provider will review your request."
        : "You declined telehealth coordination. Your case and evidence workflow are unaffected.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not record your decision"); }
    finally { setBusy(false); }
  };

  const p = state.provider;
  const consented = state.consent?.granted;
  const awaiting = state.request.status === "awaiting_consent";

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="telehealth-consent-card">
      <div className="flex items-center gap-2">
        <HeartPulse className="w-5 h-5 text-cobalt" />
        <h3 className="font-heading font-semibold text-navy">Telehealth Coordination</h3>
        <span className="ml-auto text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-slate-100 text-slate-500" data-testid="telehealth-request-status">
          {state.request.status.replace(/_/g, " ")}
        </span>
      </div>

      {p && (
        <div className="mt-3 rounded-2xl bg-slate-50 p-4 text-sm" data-testid="telehealth-provider-review">
          <div className="font-semibold text-navy">{p.practice_name} {p.verified_by_admin && <Check className="inline w-3.5 h-3.5 text-emerald-500" />}</div>
          <div className="text-xs text-slate-500 mt-0.5">{(p.provider_type || "").replace(/_/g, " ")} · {(p.states || []).join(", ") || "telehealth"} {p.languages?.length ? `· ${p.languages.join(", ")}` : ""}</div>
          {p.description && <p className="text-xs text-slate-500 mt-1">{p.description}</p>}
          <div className="text-[11px] text-slate-400 mt-1">Recommended by {state.request.firm_name}</div>
        </div>
      )}

      {awaiting ? (
        <>
          <p className="mt-3 text-xs text-slate-500" data-testid="telehealth-consent-text">{state.consent_text}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => decide("consent")} disabled={busy} data-testid="telehealth-consent-btn"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} I authorize sharing with this provider
            </button>
            <button onClick={() => decide("decline")} disabled={busy} data-testid="telehealth-decline-btn"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-medium disabled:opacity-50">
              <X className="w-4 h-4" /> No thanks
            </button>
          </div>
          <p className="mt-2 text-[10px] text-slate-400">Your consent record is stored with the date, time, document version ({state.consent_version}) and your identity.</p>
        </>
      ) : (
        <p className="mt-3 text-xs text-slate-500" data-testid="telehealth-consent-status">
          {consented
            ? (state.request.status === "accepted"
              ? `${state.request.provider_name} accepted your request and will contact you to schedule.`
              : `Consent recorded ${state.consent?.at ? new Date(state.consent.at).toLocaleString() : ""}. Waiting for the provider to respond.`)
            : "You declined telehealth coordination. Your case workflow is unaffected."}
        </p>
      )}
    </div>
  );
}
